/**
 * locale-catalog — one catalog in memory, fetched on demand.
 *
 * WHY THIS EXISTS (2026-09-12, preview memory pass). `lib/i18n.ts` used to
 * import `@v0y4ge/i18n-catalog`, whose loader statically imports EVERY locale
 * JSON under `packages/i18n-catalog/locales/` (34 files, 24.5 MB of text). That
 * put all 34 catalogs in the bundle and, worse, in every app realm's heap —
 * estimated 30–100 MB per preview realm on top of the 24.5 MB the preview
 * compiler was retaining as source. Measured effect of the whole memory pass:
 * the preview iframe held 3.1 GB.
 *
 * The shape here:
 *   · `en` is the ONLY bundled catalog — a direct JSON import of the package's
 *     `locales/en.json` (the package exports `./locales/*.json`, so this is a
 *     supported entry point, not a deep reach into its internals). It is the
 *     synchronous boot catalog and the permanent fallback, so first paint still
 *     touches no network — the 2026-07-19 §2 boot-hygiene rule holds.
 *   · every other locale is FETCHED as a resource when the user selects it,
 *     and at most CACHE_LIMIT of them are kept.
 *
 * Why fetch and not `import('./locales/' + tag + '.json')`: the preview
 * crawler (index.html) follows literal dynamic imports, so a dynamic import
 * with a literal path still pulls every catalog into the build — the exact
 * problem this file removes. A fetch is invisible to the crawler.
 *
 * WHEN THE KIT SHIPS ITS LAZY ENTRY: `@v0y4ge/i18n-catalog/lazy` and
 * `/lazy-browser` (worker-safe, announced 2026-09-12, not in this kit) replace
 * `fetchCatalog` below — keep `listAvailableLocales` reading from the package
 * at that point and delete LOCALE_TAGS.
 */
import enCatalog from "@v0y4ge/i18n-catalog/locales/en.json";

/** Flat key → ICU MessageFormat string. Structurally the kit's `Catalog`;
 *  declared locally so this module imports no value OR type from the
 *  catalog package's index — importing the index is what drags the loader
 *  (and with it all 34 JSONs) back into the graph. */
export type Catalog = Record<string, string>;

const EN = enCatalog as unknown as Catalog;

/** The locales this kit ships under `packages/i18n-catalog/locales/`, in the
 *  same order the package's own REGISTRY declares them (`qps-ploc` is
 *  deliberately absent — it is the render-gate pseudo-locale and must never
 *  appear in a tenant-facing picker). Mirrors, and is replaced by, the
 *  package's `listAvailableLocales()` once its lazy entry ships. */
const LOCALE_TAGS: readonly string[] = [
  "en", "de", "fr", "de-CH", "hi", "ja", "zh-HK", "es", "es-MX", "pt-BR", "pt-PT",
  "it", "nl", "sv", "no", "da", "fi", "pl", "cs", "sk", "hu", "ro", "el", "tr",
  "ru", "uk", "ko", "zh-CN", "th", "vi", "id", "ar", "he",
];

export function listAvailableLocales(): string[] { return LOCALE_TAGS.slice(); }
export function isKnownLocale(tag: string): boolean { return LOCALE_TAGS.includes(tag); }

/** The bundled `en` catalog — always present, the guaranteed fallback. */
export function loadDefaultCatalog(): Catalog { return EN; }

/** Where the locale JSONs are served from. The preview harness sets
 *  `__STUDIO_LOCALE_BASE__` (it serves the kit tree directly); a real build
 *  serves them from the site's `locales/` directory. */
function localeBase(): string {
  try {
    const g = globalThis as unknown as { __STUDIO_LOCALE_BASE__?: string };
    if (g.__STUDIO_LOCALE_BASE__) return g.__STUDIO_LOCALE_BASE__.replace(/\/$/, "");
  } catch { /* non-DOM env */ }
  return "/locales";
}

/** Most-recently-used catalogs, `en` excluded (it is bundled, never evicted).
 *  Three is enough for a person switching back and forth between their own
 *  language and English without re-fetching, and bounds the retained text at
 *  ~2 MB instead of 24.5 MB. */
const CACHE_LIMIT = 3;
const cache = new Map<string, Catalog>();
const inFlight = new Map<string, Promise<Catalog | undefined>>();

function touch(tag: string, catalog: Catalog): void {
  cache.delete(tag);
  cache.set(tag, catalog);
  while (cache.size > CACHE_LIMIT) {
    const oldest = cache.keys().next();
    if (oldest.done) break;
    cache.delete(oldest.value);
  }
}

/** The catalog for `tag` if it is already in memory — `en` always, plus any
 *  recently used locale. Synchronous, so the render path can use it without
 *  waiting; `undefined` means "fetch it". */
export function loadedCatalog(tag: string): Catalog | undefined {
  if (tag === "en") return EN;
  const hit = cache.get(tag);
  if (hit) { touch(tag, hit); return hit; }
  return undefined;
}

/** Resolve `tag`'s catalog, fetching it once if needed. `undefined` when the
 *  tag isn't shipped or the fetch fails — callers keep the English fallback,
 *  which is already applied, so this can never blank the UI. */
export async function fetchCatalog(tag: string): Promise<Catalog | undefined> {
  const loaded = loadedCatalog(tag);
  if (loaded) return loaded;
  if (!isKnownLocale(tag)) return undefined;
  const pending = inFlight.get(tag);
  if (pending) return pending;
  const p = (async () => {
    try {
      const res = await fetch(`${localeBase()}/${tag}.json`, { headers: { accept: "application/json" } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = (await res.json()) as Catalog;
      touch(tag, json);
      return json;
    } catch (e) {
      console.warn(`i18n: could not load the ${tag} catalog — staying on English:`, e);
      return undefined;
    } finally {
      inFlight.delete(tag);
    }
  })();
  inFlight.set(tag, p);
  return p;
}

/** Diagnostics for the memory pass: what is actually resident right now. */
export function catalogCacheStats(): { bundled: string[]; cached: string[]; limit: number } {
  return { bundled: ["en"], cached: Array.from(cache.keys()), limit: CACHE_LIMIT };
}
